import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Sentry error tracking (must be first)
import { initSentry } from './utils/sentry.js';
initSentry();

// Mobile optimizations & smooth scrolling
import './styles/mobile.css';

import App from './App.js';
import ErrorBoundary from './components/common/ErrorBoundary.js';
import ErrorLogViewer from './components/common/ErrorLogViewer.js';
import { AuthProvider } from './context/AuthContext.js';
import { CartProvider } from './context/CartContext.js';
import { CompareProvider } from './context/CompareContext.js';
import { FavoritesProvider } from './context/FavoritesContext.js';
import { I18nProvider } from './context/I18nContext.js';
import { OrganizationProvider } from './context/OrganizationContext.js';
import { QuickViewProvider } from './context/QuickViewContext.js';
import { RatesProvider } from './context/RatesContext.js';
import { RecentlyViewedProvider } from './context/RecentlyViewedContext.js';
import { ReviewsProvider } from './context/ReviewsContext.js';
import { installGlobalErrorHandlers } from './setupGlobalErrorHandling.js';
import theme from './theme/index.js';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

installGlobalErrorHandlers();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <I18nProvider>
            <RatesProvider>
              <OrganizationProvider>
                <AuthProvider>
                <CartProvider>
                  <FavoritesProvider>
                    <CompareProvider>
                      <RecentlyViewedProvider>
                        <QuickViewProvider>
                          <ReviewsProvider>
                            <ErrorBoundary>
                              <App />
                              <ErrorLogViewer />
                            </ErrorBoundary>
                          </ReviewsProvider>
                        </QuickViewProvider>
                      </RecentlyViewedProvider>
                    </CompareProvider>
                  </FavoritesProvider>
                  <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                  />
                </CartProvider>
              </AuthProvider>
            </OrganizationProvider>
            </RatesProvider>
          </I18nProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// Register basic service worker (progressive enhancement)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.debug('SW registration failed', err);
    });
  });
}
